import React, { useState } from "react";
import { Container, Box, ThemeProvider } from "@mui/material";
import { createTinyTheme as createAlbertTheme } from "@tiny/theme";
import { createTinyTheme as createEmailTheme } from "@tiny/email-theme";
import { useConfig } from "./Config.hooks";
import { ConfigProvider } from "./Config.context";
import { TinyCommonsProps } from "./App.types";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ErrorBoundary from "./components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "react-query";
import { initializeApi } from "./app/apiInstance";
import { LoadingSnackbarProvider } from "./components/LoadingSnackbar/LoadingSnackbar.context";
import { enqueueSnackbar } from "notistack";
import {
  extractBackendError,
  extractErrorMessage,
  getErrorMessage,
  isManagedError,
} from "./utils/errorUtils";
import { TinyDialogProvider } from "@tiny/lib";
import RefreshProvider from "./components/RefreshProvider";
import DashboardPage from "./pages/Dashboard/index";
import CustomerHistory from "./pages/CustomerHistory";

const App = ({ config: importedConfig }: TinyCommonsProps) => {
  const { config, setConfig } = useConfig();
  const albertTheme = createAlbertTheme();
  const emailTheme = createEmailTheme();

  const [apiInitialized, setApiInitialized] = useState(false);

  React.useEffect(() => {
    initializeApi(importedConfig.baseUrl);
    setApiInitialized(true);
  }, [importedConfig.baseUrl]);

  if (!apiInitialized) {
    return null;
  }

  return (
    <AppQueryClientProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ConfigProvider
          value={{
            config: {
              ...config,
              ...importedConfig,
            },
            setConfig,
          }}
        >
          <ThemeProvider
            theme={
              importedConfig.theme ??
              (importedConfig.appName === "tinyEmail"
                ? emailTheme
                : albertTheme)
            }
          >
            <TinyDialogProvider>
              {/* No nested SnackbarProvider here — this widget is always
                  embedded inside a host app (email/einstein) that already
                  mounts its own configured SnackbarProvider. A second,
                  unstyled notistack SnackbarProvider would steal the
                  module-level enqueueSnackbar/closeSnackbar singleton away
                  from the host's the moment this mounts, and never give it
                  back on unmount, silently breaking every toast elsewhere
                  in the host app for the rest of the session. */}
              <LoadingSnackbarProvider>
                <ErrorBoundary>
                  <RefreshProvider>
                    {importedConfig.pageName === "dashboard" && (
                      <DashboardPage />
                    )}
                    {importedConfig.pageName === "customerHistory" && (
                      <CustomerHistory />
                    )}
                  </RefreshProvider>
                </ErrorBoundary>
              </LoadingSnackbarProvider>
            </TinyDialogProvider>
          </ThemeProvider>
        </ConfigProvider>
      </LocalizationProvider>
    </AppQueryClientProvider>
  );
};
export default App;

function AppQueryClientProvider({ children }: { children: React.ReactNode }) {
  const onError = React.useCallback(
    (e: any) => {
      extractBackendError(e.response).then((be) => {
        const errorCodes = be?.errorCodes;
        if (!isManagedError(errorCodes)) {
          enqueueSnackbar(extractErrorMessage(be) ?? getErrorMessage(be), {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
            autoHideDuration: 10000,
          });
        }
      });
    },
    [enqueueSnackbar]
  );

  const queryClient = React.useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: Infinity,
          cacheTime: 0,
          retry: false,
          refetchOnWindowFocus: false,
          onError,
        },
        mutations: {
          onError,
        },
      },
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient} {...({} as any)}>
      {children}
    </QueryClientProvider>
  );
}
